import { ApiResponse } from './types';

export interface PowerFacility {
  Type: string;
  'Country/area': string;
  Subregion: string;
  Region: string;
  'Plant / Project name': string;
  'Unit / Phase name'?: string | null;
  'Capacity (MW)': number;
  Status: 'operating' | 'announced' | 'construction' | 'cancelled' | 'mothballed' | 'retired';
  'Start year'?: number | null;
  Technology?: string | null;
  Fuel?: string | null;
  Latitude?: number | null;
  Longitude?: number | null;
  City?: string | null;
  'Subnational unit (state, province)'?: string | null;
  Owner?: string | null;
}

export interface PowerFacilitiesResponse {
  facilities: PowerFacility[];
  total_count: number;
}

export type ForecastType = 'solar' | 'wind_onshore' | 'wind_offshore' | 'load';
export type GermanTSO = 'TransnetBW' | '50Hertz' | 'TenneT' | 'Amprion';
export type TimeGranularity = 'yearly' | 'monthly';
export type DataType = 'total' | 'delta';

export interface SolarForecastValue {
  timestamp: string;
  actual_value: number;
  day_ahead_forecast: number;
  intraday_forecast?: number | null;
  current_forecast?: number | null;
}

export interface WindOnshoreForecastValue {
  timestamp: string;
  actual_value: number;
  day_ahead_forecast: number;
  intraday_forecast?: number | null;
  current_forecast?: number | null;
}

export interface WindOffshoreForecastValue {
  timestamp: string;
  actual_value: number;
  day_ahead_forecast: number;
  intraday_forecast?: number | null;
  current_forecast?: number | null;
}

export interface LoadForecastValue {
  timestamp: string;
  actual_value: number;
  day_ahead_forecast: number;
  intraday_forecast?: number | null;
  current_forecast?: number | null;
}

export interface SolarForecastResponse {
  country_code: string;
  tso?: GermanTSO | null;
  forecast_type: ForecastType;
  data: SolarForecastValue[];
}

export interface WindOnshoreForecastResponse {
  country_code: string;
  tso?: GermanTSO | null;
  forecast_type: ForecastType;
  data: WindOnshoreForecastValue[];
}

export interface WindOffshoreForecastResponse {
  country_code: string;
  tso?: GermanTSO | null;
  forecast_type: ForecastType;
  data: WindOffshoreForecastValue[];
}

export interface LoadForecastResponse {
  country_code: string;
  tso?: GermanTSO | null;
  forecast_type: ForecastType;
  data: LoadForecastValue[];
}

export interface PowerDataValue {
  timestamp: string;
  nuclear?: number | null;
  fossil_brown_coal?: number | null;
  fossil_hard_coal?: number | null;
  fossil_gas?: number | null;
  fossil_oil?: number | null;
  wind_offshore?: number | null;
  wind_onshore?: number | null;
  solar?: number | null;
  biomass?: number | null;
  geothermal?: number | null;
  hydro_run_of_river?: number | null;
  hydro_water_reservoir?: number | null;
  hydro_pumped_storage?: number | null;
  hydro_pumped_storage_consumption?: number | null;
  waste?: number | null;
  other_renewables?: number | null;
  others?: number | null;
  load?: number | null;
  residual_load?: number | null;
  cross_border_trading?: number | null;
  import_balance?: number | null;
  renewable_share_load?: number | null;
  renewable_share_generation?: number | null;

  // Forecasts
  generation_forecast?: number | null;
  wind_offshore_forecast?: number | null;
  wind_onshore_forecast?: number | null;
  solar_forecast?: number | null;
  load_forecast?: number | null;
  residual_load_forecast?: number | null;
  renewable_share_load_forecast?: number | null;
  renewable_share_generation_forecast?: number | null;

  // Additional source forecasts
  hydro_run_of_river_forecast?: number | null;
  biomass_forecast?: number | null;
  fossil_brown_coal_forecast?: number | null;
  fossil_hard_coal_forecast?: number | null;
  fossil_oil_forecast?: number | null;
  fossil_gas_forecast?: number | null;
  hydro_water_reservoir_forecast?: number | null;
  hydro_pumped_storage_forecast?: number | null;
  waste_forecast?: number | null;
}

export interface PowerDataResponse {
  country_code: string;
  data: PowerDataValue[];
}

export interface InstalledCapacityValue {
  timestamp: string;
  nuclear?: number | null;
  fossil_brown_coal?: number | null;
  fossil_hard_coal?: number | null;
  fossil_coal_derived_gas?: number | null;
  fossil_gas?: number | null;
  fossil_oil?: number | null;
  fossil_oil_shale?: number | null;
  fossil_peat?: number | null;
  wind_offshore?: number | null;
  wind_onshore?: number | null;
  solar?: number | null;
  biomass?: number | null;
  geothermal?: number | null;
  marine?: number | null;
  hydro_run_of_river?: number | null;
  hydro_water_reservoir?: number | null;
  hydro_pumped_storage?: number | null;
  battery_storage_power?: number | null;
  battery_storage_capacity?: number | null;
  waste?: number | null;
  other_renewables?: number | null;
  others?: number | null;
  other_non_renewable?: number | null;
}

export interface InstalledCapacityResponse {
  country_code: string;
  country_name: string;
  granularity: TimeGranularity;
  data_type: DataType;
  start_time: string;
  end_time: string;
  data: InstalledCapacityValue[];
}

export class PowerService {
  constructor(
    private baseUrl: string,
    private getToken: () => Promise<string>,
  ) {}

  private async fetchWithAuth(path: string, init?: RequestInit): Promise<Response> {
    const token = await this.getToken();
    return fetch(`${this.baseUrl}/v1${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...init?.headers,
      },
    });
  }

  // PowerFacilities Endpoints
  async getPowerFacilities(
    params: {
      country?: string;
      facility_type?: string;
      status?: string;
      min_capacity?: number;
      max_capacity?: number;
    } = {},
  ): Promise<ApiResponse<PowerFacilitiesResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.country) queryParams.append('country', params.country);
      if (params.facility_type) queryParams.append('facility_type', params.facility_type);
      if (params.status) queryParams.append('status', params.status);
      if (params.min_capacity) queryParams.append('min_capacity', params.min_capacity.toString());
      if (params.max_capacity) queryParams.append('max_capacity', params.max_capacity.toString());

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await this.fetchWithAuth(`/power_facilities/power-facilities${queryString}`);

      if (!response.ok) {
        throw new Error(`Failed to get power facilities: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get power facilities',
      };
    }
  }

  async getPowerFacilityCountries(): Promise<ApiResponse<string[]>> {
    try {
      const response = await this.fetchWithAuth('/power_facilities/power-facilities/countries');

      if (!response.ok) {
        throw new Error(`Failed to get facility countries: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get facility countries',
      };
    }
  }

  async getPowerFacilityTypes(): Promise<ApiResponse<string[]>> {
    try {
      const response = await this.fetchWithAuth('/power_facilities/power-facilities/types');

      if (!response.ok) {
        throw new Error(`Failed to get facility types: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get facility types',
      };
    }
  }

  // Power Forecasts Endpoints
  async getPowerForecasts(params: {
    country_code: string;
    forecast_type: ForecastType;
    start_time?: string;
    end_time?: string;
    latest?: boolean;
    tso?: GermanTSO;
  }): Promise<
    ApiResponse<
      | SolarForecastResponse
      | WindOnshoreForecastResponse
      | WindOffshoreForecastResponse
      | LoadForecastResponse
    >
  > {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('country_code', params.country_code);
      queryParams.append('forecast_type', params.forecast_type);

      // Either use latest=true or provide start_time and end_time
      // Default to latest=true if not specified
      if (params.latest !== false && (!params.start_time || !params.end_time)) {
        queryParams.append('latest', 'true');
      } else if (params.start_time && params.end_time) {
        queryParams.append('start_time', params.start_time);
        queryParams.append('end_time', params.end_time);
        // Only explicitly set latest=false if start_time and end_time are provided
        if (params.latest === false) {
          queryParams.append('latest', 'false');
        }
      } else {
        throw new Error('Either use latest data or provide both start_time and end_time');
      }

      if (params.tso) queryParams.append('tso', params.tso);

      const response = await this.fetchWithAuth(
        `/power_forecasts/power-forecasts?${queryParams.toString()}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to get power forecasts: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get power forecasts',
      };
    }
  }

  // ------------------------------------------------------------------
  // Supabase (Jua) Wind-Onshore Forecast Endpoint
  // ------------------------------------------------------------------
  async getSupabaseWindOnshoreForecast(): Promise<ApiResponse<WindOnshoreForecastResponse>> {
    try {
      const response = await this.fetchWithAuth('/power_forecasts/supabase/wind-onshore');

      if (!response.ok) {
        throw new Error(`Failed to get Supabase wind-onshore forecast: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error ? error.message : 'Failed to get Supabase wind-onshore forecast',
      };
    }
  }

  // ------------------------------------------------------------------
  // Supabase (Jua) Solar Forecast Endpoint
  // ------------------------------------------------------------------
  async getSupabaseSolarForecast(): Promise<ApiResponse<SolarForecastResponse>> {
    try {
      const response = await this.fetchWithAuth('/power_forecasts/supabase/solar');

      if (!response.ok) {
        throw new Error(`Failed to get Supabase solar forecast: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get Supabase solar forecast',
      };
    }
  }

  async getPowerForecastCountries(): Promise<ApiResponse<Record<string, string>>> {
    try {
      const response = await this.fetchWithAuth('/power_forecasts/countries');

      if (!response.ok) {
        throw new Error(`Failed to get forecast countries: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get forecast countries',
      };
    }
  }

  // Power Data Endpoints
  async getPowerData(params: {
    country_code: string;
    start_time?: string;
    end_time?: string;
    latest?: boolean;
  }): Promise<ApiResponse<PowerDataResponse>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('country_code', params.country_code);

      // Either use latest=true or provide start_time and end_time
      // Similar to the getPowerForecasts method
      if (params.latest) {
        queryParams.append('latest', 'true');
      } else if (params.start_time && params.end_time) {
        queryParams.append('start_time', params.start_time);
        queryParams.append('end_time', params.end_time);
      } else {
        throw new Error('Either use latest=true or provide both start_time and end_time');
      }

      const response = await this.fetchWithAuth(`/power_data/power-data?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to get power data: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get power data',
      };
    }
  }

  async getPowerDataCountries(): Promise<ApiResponse<Record<string, string>>> {
    try {
      const response = await this.fetchWithAuth('/power_data/countries');

      if (!response.ok) {
        throw new Error(`Failed to get power data countries: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get power data countries',
      };
    }
  }

  // Installed Capacity Endpoints
  async getInstalledCapacity(params: {
    country_code: string;
    granularity: TimeGranularity;
    data_type: DataType;
    start_time: string;
    end_time: string;
  }): Promise<ApiResponse<InstalledCapacityResponse>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('country_code', params.country_code);
      queryParams.append('granularity', params.granularity);
      queryParams.append('data_type', params.data_type);
      queryParams.append('start_time', params.start_time);
      queryParams.append('end_time', params.end_time);

      const response = await this.fetchWithAuth(
        `/installed_capacity/installed-capacity?${queryParams.toString()}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to get installed capacity: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get installed capacity',
      };
    }
  }

  async getInstalledCapacityCountries(): Promise<ApiResponse<Record<string, string>>> {
    try {
      const response = await this.fetchWithAuth('/installed_capacity/countries');

      if (!response.ok) {
        throw new Error(`Failed to get installed capacity countries: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error ? error.message : 'Failed to get installed capacity countries',
      };
    }
  }
}
